<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout" v-if="isLoading" :class="{ hideVerticalScrollbar: hideVerticalScrollbar }">
      <router-view />
    </component>

    <loader :visible="isLoading" />
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

import { setDomain } from '@modv/sdk'

import Vue from 'vue'
import { mapState } from 'vuex'
import axios from 'axios'
import VueAxios from 'vue-axios'
import VueLuxon from 'vue-luxon'
import { setAxiosInstance, getAxiosInstance } from '@modv/sdk'

import { getInstance } from './auth'
import router from './router'
import Loader from '@/components/Loader'

import VueCookie from 'vue-cookie'
import { Message } from 'element-ui'

Vue.use(VueCookie)

// import VueSocketIO from './libs/socket/index'

Vue.use(VueAxios, axios)
Vue.use(VueLuxon, {
  input: {
    zone: 'utc',
    format: 'millis'
  },
  output: 'short'
})

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

async function brandProcess() {
  const { brandId } = this.$route.query
  if (brandId) {
    await this.$store.dispatch('organisation/getBrand', { brandId })
    console.log(this.brand)
    document.body.style.setProperty('--primary', this.brand.primaryColor)
    console.log(document.body.style)
  }
}

export default {
  components: {
    LayoutVertical,
    LayoutFull,
    Loader
  },
  data() {
    return {
      isLoading: false,
      sessionExpiresOn: 0,
      sessionExpiredSetTimeOut: null,
      isActionMenuVisible: false,
      fabGlobalOptions: {
        spacing: 60,
        delay: 0.1
      },
      fabActions: [
        {
          name: 'cache',
          icon: 'cached'
        },
        {
          name: 'alertMe',
          icon: 'add_alert'
        }
      ]
    }
  },
  computed: {
    ...mapState('loader', ['loading']),
    ...mapState('app', ['hideVerticalScrollbar']),
    ...mapState('organisation', ['brand']),
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    isIdle() {
      return this.$store.state.idleVue.isIdle
    }
  },
  watch: {
    $route(to, from) {
      if (this.$route.params.logout !== '') {
        if (this.$router.currentRoute.path === '/register') {
          sessionStorage.clear()
        } else if (this.$router.currentRoute.path !== '/login') {
          const instance = getInstance()
          if (!instance.isAuthenticated && !instance.loading && process.env.VUE_APP_ENABLE_AUTH0 === 'true') {
            // Force to login page
            router.push('login')
          } else if (!sessionStorage.getItem('jwt') && process.env.VUE_APP_ENABLE_AUTH0 === 'false') {
            // Force to login page
            router.push('login')
          }
        }

        if (this.$router.currentRoute.path === '/logout') {
          // Clear any cache
          sessionStorage.clear()
          this.$cookie.delete('jwt')
          this.$auth.logout({
            returnTo: window.location.origin
          })
        } else {
          // close modals and clear the details
          this.$store.dispatch('organisationDetailsModal/onChangeModal', {
            self: this,
            visible: false
          })
          this.$store.dispatch('groupDetailsModal/onChangeModal', {
            self: this,
            visible: false
          })
        }
      }
    },
    isIdle() {
      if (this.$store.state.idleVue.isIdle && this.$router.currentRoute.path !== '/login') {
        this.$bvToast.toast('You have left this browser idle for 10 minutes.', {
          title: `Session expires soon and you will be logged out`,
          variant: 'warning',
          solid: true,
          noAutoHide: true
        })

        this.sessionExpiredSetTimeOut = setTimeout(() => {
          // Clear any cache
          sessionStorage.clear()
          sessionStorage.setItem('sessionTimeOut', true)
          this.$cookie.delete('jwt')

          this.$auth.logout({
            returnTo: window.location.origin
          })
        }, 60 * 10 * 1000) // 10 mins
      }
    },
    isActive() {
      if (this.$store.state.idleVue.isActive) {
        clearTimeout(this.timeoutDbClick)
        this.sessionExpiredSetTimeOut = null
      }
    }
  },
  async beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  created() {
    if (this.$workbox) {
      this.$workbox.addEventListener('waiting', () => {
        this.showUpdateUI = true
      })
    }
  },
  async accept() {
    this.showUpdateUI = false
    await this.$workbox.messageSW({ type: 'SKIP_WAITING' })
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade'
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    setDomain(process.env.VUE_APP_API_HOST)

    return {
      skinClasses
    }
  },
  async mounted() {
    let isAuthenticated = false

    const authPromise = new Promise(async resolve => {
      const instance = getInstance()

      instance.$watch('loading', async loading => {
        if (!loading) {
          if (instance.isAuthenticated) {
            try {
              const token = await instance.getTokenSilently()
              const profile = await instance.getIdTokenClaims(token)

              // Best to move the Axios interceptor code to its own file if possible
              const axiosInstance = axios.create()

              // Inject JWT token
              axiosInstance.interceptors.request.use(
                config => {
                  config.headers['Authorization'] = `Bearer ${profile.__raw}`
                  return config
                },
                error => {
                  return Promise.reject(error)
                }
              )

              setAxiosInstance(axiosInstance)

              await this.$store.dispatch('auth/authorize', { self: this })

              // Inject legacy token for now
              axiosInstance.interceptors.request.use(
                config => {
                  config.headers['jwt'] = `${this.$store.getters['auth/jwt']}`
                  return config
                },
                error => {
                  return Promise.reject(error)
                }
              )

              axiosInstance.interceptors.response.use(
                value => {
                  return value
                },
                error => {
                  const data = error.response.data
                  if (typeof data === 'string') {
                    Message.error(data)
                  } else if (data.message) {
                    Message.error(data.message)
                  }
                  return Promise.reject(error)
                }
              )

              // Vue.use(
              //   new VueSocketIO({
              //     debug: true,
              //     connection: process.env.VUE_APP_WSS_HOST,
              //     options: {
              //       path: '',
              //       query: {
              //         token: this.$store.getters['auth/jwt']
              //       }
              //     }
              //   })
              // )

              // this.$socket.onAny((event, ...args) => {
              //   console.log(event, args)
              // })

              // await this.$store.getters['chat/wss/isConnected']
              // heartbeat(this.$socket)
            } catch (err) {
              // Sink errors here and log them out
              console.error(err)
            }
          } else {
            if (process.env.VUE_APP_ENABLE_AUTH0 === 'true') {
              if (this.$router.currentRoute.path === '/register') {
                // Going to register page
                sessionStorage.clear()
                this.$cookie.delete('jwt')
              } else {
                // Force to login page
                router.push('login')
              }

              isAuthenticated = instance.isAuthenticated
            } else {
              // Check if JWT exists and if so assign back to sessionStorage
              const jwt = this.$cookie.get('jwt')
              if (jwt) {
                sessionStorage.setItem('jwt', jwt)
                isAuthenticated = true

                const axiosInstance = getAxiosInstance()

                // Inject legacy token for now
                axiosInstance.interceptors.request.use(
                  config => {
                    config.headers['jwt'] = jwt
                    return config
                  },
                  error => {
                    return Promise.reject(error)
                  }
                )

                setAxiosInstance(axiosInstance)
              } else {
                sessionStorage.clear()
                this.$cookie.delete('jwt')
                router.push('login')
                isAuthenticated = false
              }
            }
          }

          resolve()
        }
      })
    })

    await authPromise
    this.isLoading = true
    if (isAuthenticated) {
      const profile = (await this.$store.dispatch('userProfile/load', { self: this })).data
      // Check that the user either an Admin or OrgAdmin
      if (!profile.isAdmin && !profile.isOrgAdmin) {
        sessionStorage.setItem('isUnauthorisedAccess', true)
        this.$auth.logout({
          returnTo: window.location.origin
        })
      }
    }
    await brandProcess.call(this)
  }
}
</script>

<style lang="scss">
@import '~@core/scss/base/pages/app-file-manager.scss';
@import './assets/scss/variables/_variables.scss';

.hideVerticalScrollbar {
  overflow: hidden;
}

.header-navbar {
  display: none !important;
  margin: 0 !important;
}

.content.app-content {
  padding: 2rem !important;
}

.header-navbar-shadow {
  display: none !important;
}

.display-flex {
  display: flex;
}

.display-inline {
  display: inline;
  color: $purple;
  margin-right: 1rem;
}

.text-muted {
  color: $text-muted;
}

.modal .modal-header .close {
  margin: 0% !important;
}

.files-section-title {
  @media (max-width: 576px) {
    margin-left: 2rem;
  }
}

.tab-content {
  margin-bottom: 1rem;
  height: calc(100% - 67px);
}

@media screen and (min-width: 320px) and (max-width: 1200px) {
  html {
    overflow: hidden;
    height: 100%;
  }

  body {
    height: 100%;
    overflow: auto;
  }

  .fab-main-container {
    bottom: 20% !important;
  }

  .vue-fab-iconfont-icons {
    font-size: 1.8rem !important;
  }

  .vue-fab-iconfont-icons-sub-item {
    margin-left: 7px;
    margin-top: 7px;
    color: $purple;
  }
}
</style>
